import * as React from "react"
import { useCallback, useMemo, useRef, useState } from "react"
import {
  Appearance, LayoutChangeEvent, LayoutRectangle,
  TextInput,
  TouchableOpacity,
  ViewStyle,
} from "react-native"
import { observer } from "mobx-react-lite"
import { Icon, View, Checkbox } from "native-base"
import MaterialIcons from "react-native-vector-icons/MaterialIcons"
import { color, nativeBaseTheme } from "../../theme"
import { Input, InputProps } from "../input/input"
import { JalaIcon } from "../jala-icon/jala-icon"
import { TxKeyPath } from "../../i18n"
import { Button } from "../button/button"
import { Actionsheet, ActionsheetProps } from "../actionsheet/actionsheet"
import { FlatList, ListRenderItem, FlatListProps } from "../flat-list/flat-list"
import { SafeAreaView } from "react-native-safe-area-context"
import { JalaLoading } from "../jala-loading/jala-loading"

// @ts-ignore
export interface PickerFlatListProps<ItemT extends any> extends FlatListProps<any> {
  data?: ReadonlyArray<ItemT> | null | undefined;
  renderItem?: ListRenderItem<ItemT> | null | undefined;
}

export interface PickerActionsheetProps extends ActionsheetProps {
  data?: ReadonlyArray<any> | null | undefined,
  onPressItem?: (item: any) => (() => void) | undefined,
  flatListProps?: PickerFlatListProps<any>,
  renderItem: ListRenderItem<any> | null | undefined,

  inputProps?: InputProps,

  searchDefaultValue?: string,
  withoutSearch?: boolean,
  isUseButton?: boolean
  onPressBottomButton?: () => void,
  buttonTx?: TxKeyPath,
  buttonTestId?: string,
  isCheckModal?: boolean,
  isDisabled?: boolean,
  selectedChecked?: ReadonlyArray<any> | null | undefined,
  loading?: boolean
}

const SEARCH_CONTAINER: ViewStyle = {
  flexDirection: "row",
  paddingHorizontal: 16
}

const SEARCH_INPUT: ViewStyle = {
  flexGrow: 1,
  height: "40px",
  paddingVertical: 6
}

const SEPARATOR: ViewStyle = {
  height: 7,
  borderBottomWidth: 1,
  borderColor: color.palette.gray200
}

const ACTIONSHEET_ITEM: ViewStyle = {
  paddingTop: 0,
  paddingBottom: 0,
  paddingLeft: 0,
  paddingRight: 0
}

const BUTTON: ViewStyle = {
  marginHorizontal: 16,
  marginVertical: 10
}

const CHECKBOX_ITEM: ViewStyle = {
  paddingTop: 0,
  paddingBottom: 0,
  paddingLeft: 0,
  paddingRight: 0,
  marginLeft: 16
}

/**
 * Describe your component here
 */
export const PickerActionsheet = observer(function PickerActionsheet(props: PickerActionsheetProps) {
  const {
    onClose: onCloseProp, data, onPressItem, flatListProps: { ListEmptyComponent, ...flatListPropsRest}, renderItem, inputProps = {},
    withoutSearch, searchDefaultValue, isCheckModal, selectedChecked, isDisabled, loading,
    colorScheme = Appearance.getColorScheme(), onPressBottomButton, isUseButton, buttonTx, buttonTestId,
    ...rest
  } = props

  const [ flatListLayout, setFlatListLayout ] = useState<LayoutRectangle>(null)
  const onLayout: (e: LayoutChangeEvent) => void = useCallback((e) => {
    setFlatListLayout(e.nativeEvent.layout)
  }, [])

  const { onChangeText } = inputProps || {}

  const inputRef = useRef<TextInput>()

  const iconBaseStyle = useMemo(() => nativeBaseTheme.components.Icon.baseStyle({ colorMode: colorScheme }), [colorScheme])

  const clearSearchInput = useCallback(() => {
    if (inputRef.current) inputRef.current.clear()
    onChangeText && onChangeText("")
  }, [onChangeText])

  const onClose = useCallback(() => {
    clearSearchInput()
    onCloseProp && onCloseProp()
  }, [clearSearchInput, onCloseProp])

  const onPressActionsheetItem = useCallback((item) => () => {
    onPressItem(item)()
    onClose()
  }, [onClose, onPressItem])

  const checkBoxItem = useCallback((info) => {
    const isChecked = !!(selectedChecked.length && selectedChecked.find((i) => i === info.item.id))
    return (
      <View style={CHECKBOX_ITEM}>
        <Checkbox
          value={info}
          onChange={() => onPressItem(info.item)()}
          colorScheme="blue"
          defaultIsChecked={isChecked}
          isChecked={isChecked}
          // isDisabled={isDisabled}
        >
          {renderItem(info)}
        </Checkbox>
      </View>
    )
  }, [onPressItem, renderItem, selectedChecked])

  const renderActionsheetItem: ListRenderItem<any> = useCallback((info) => (
    <TouchableOpacity testID="jala:id/picker-options" style={ACTIONSHEET_ITEM} onPress={onPressActionsheetItem(info.item)}>
      {renderItem(info)}
    </TouchableOpacity>
  ), [onPressActionsheetItem, renderItem])

  const renderItemUsed: ListRenderItem<any> = useCallback((info) => {
    return isCheckModal ? checkBoxItem(info) : renderActionsheetItem(info);
  }, [checkBoxItem, isCheckModal, renderActionsheetItem])

  const onClear = useCallback(() => {
    onPressItem(undefined)()
    onClose()
  }, [onClose, onPressItem])

  const onPressButton = useCallback(() => {
    onPressBottomButton()
    onCloseProp && onCloseProp()
  }, [onCloseProp, onPressBottomButton])

  const keyExtractor = useCallback((item, index) => `${index}`, [])

  return (
    <Actionsheet
      onClose={onClose}
      onClear={onClear}
      colorScheme={colorScheme}
      {...rest}
    >
      {!withoutSearch && <View style={SEARCH_CONTAINER} >
        <View flexGrow={1}>
          <Input
            colorScheme={colorScheme}
            testID="jala:id/picker-search-input"
            ref={inputRef}
            type="text"
            autoCapitalize="none"
            autoComplete="off"
            autoCorrect={false}
            textContentType="none"
            keyboardType="default"
            returnKeyType="search"
            placeholderTx="Search"
            defaultValue={searchDefaultValue || ''}
            InputLeftElement={<Icon as={<MaterialIcons name="search" />} size="24px" ml="9px" textAlign="center" color={color.palette.gray500} />}
            style={SEARCH_INPUT}
            {...inputProps}
            _invalid={{ borderColor: nativeBaseTheme.components.InputBase.baseStyle({}).borderColor }}
          />
        </View>
        <View justifyContent="center">
          <Icon
            {...iconBaseStyle}
            as={<JalaIcon name="danger" />}
            size="20px"
            ml="12px"
            mr="4px"
            textAlign="center"
            onPress={clearSearchInput}
          ></Icon>
        </View>
      </View>}
      <View style={SEPARATOR}></View>
      <FlatList
        data={data}
        renderItem={renderItemUsed}
        keyExtractor={keyExtractor}
        keyboardShouldPersistTaps="handled"
        ListEmptyComponent={flatListLayout && loading && (
          <View flexGrow={1} alignItems="center" justifyContent="center" height={`${flatListLayout.height}px`}>
            <JalaLoading />
          </View>
        ) || ListEmptyComponent || null}
        ListFooterComponent={<SafeAreaView edges={['bottom']} />}
        onLayout={onLayout}
        {...flatListPropsRest}
      />
      {isUseButton && <Button
        testID={buttonTestId}
        tx={buttonTx}
        colorScheme={colorScheme}
        style={BUTTON}
        // disabled={isError}
        // loading={tokenLoading}
        onPress={onPressButton}
      />}
    </Actionsheet>
  )
})
